import { useEffect, useRef, useState } from "react";
import Button from "../../../components/Button";
import { api } from "../../../helpers/api";

type StripeCustomerOption = {
  id: string;
  name: string | null;
  email: string | null;
  paymentMethod?: { brand: string; last4: string } | null;
};

type LinkStripeCustomerModalProps = {
  userId: string;
  userName: string;
  userEmail: string;
  currentStripeCustomerId?: string;
  onClose: () => void;
  onSave: (data: { userId: string; stripeCustomerId: string }) => void;
  isPending: boolean;
};

export function LinkStripeCustomerModal({
  userId,
  userName,
  userEmail,
  currentStripeCustomerId,
  onClose,
  onSave,
  isPending,
}: LinkStripeCustomerModalProps) {
  const [customers, setCustomers] = useState<StripeCustomerOption[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");
  const isRelink = Boolean(currentStripeCustomerId);
  const [selectedCustomerId, setSelectedCustomerId] = useState(
    currentStripeCustomerId ?? "",
  );
  const [isOpen, setIsOpen] = useState(false);
  const [searchFilter, setSearchFilter] = useState("");
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let cancelled = false;

    const fetchCustomers = async () => {
      try {
        setIsLoading(true);
        setError("");
        const data = await api.commissionBilling.searchStripeCustomers();
        if (!cancelled) {
          const sorted = (Array.isArray(data) ? data : []).sort((a, b) =>
            (a.name || "").localeCompare(b.name || "", undefined, { sensitivity: "base" })
          );
          setCustomers(sorted);
        }
      } catch (err: any) {
        if (!cancelled) {
          setError(
            err.response?.data?.message || "Failed to search Stripe customers",
          );
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    };

    fetchCustomers();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (currentStripeCustomerId) {
      setSelectedCustomerId(currentStripeCustomerId);
    }
  }, [currentStripeCustomerId]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target as Node)
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleSave = () => {
    if (!selectedCustomerId) return;
    onSave({ userId, stripeCustomerId: selectedCustomerId });
  };

  const searchInputRef = useRef<HTMLInputElement>(null);

  const handleSelect = (customerId: string) => {
    setSelectedCustomerId(customerId);
    setSearchFilter("");
    setIsOpen(false);
  };

  const selectedCustomer = customers.find((c) => c.id === selectedCustomerId);

  const filteredCustomers = customers.filter((c) => {
    if (!searchFilter) return true;
    const q = searchFilter.toLowerCase();
    return (
      (c.name || "").toLowerCase().includes(q) ||
      (c.email || "").toLowerCase().includes(q)
    );
  });

  const formatCustomer = (customer: StripeCustomerOption) => {
    const pm = customer.paymentMethod;
    const cardInfo = pm ? `${pm.brand} •••• ${pm.last4}` : undefined;
    const email = customer.email ?? "";
    const emailPrefix = email.split("@")[0];
    return {
      name:
        customer.name ||
        (emailPrefix ? `${emailPrefix} Hotel` : customer.id),
      detail:
        [email, cardInfo].filter(Boolean).join(" — ") || "No email on file",
    };
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg w-full max-w-md shadow-xl">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-content-primary">
            {isRelink ? "Change Stripe Customer" : "Link Stripe Customer"}
          </h3>
        </div>

        {/* Content */}
        <div className="px-6 py-4 space-y-4">
          {/* User info */}
          <div className="bg-gray-50 rounded-lg p-3">
            <div className="text-sm font-medium text-content-primary">
              {userName}
            </div>
            <div className="text-xs text-content-tertiary">{userEmail}</div>
          </div>

          {isRelink && (
            <div
              data-testid="currently-linked"
              className="bg-amber-50 border border-amber-200 rounded-lg p-3"
            >
              <div className="text-xs font-semibold text-amber-800 uppercase tracking-wide mb-1">
                Currently linked
              </div>
              {isLoading ? (
                <div className="text-sm text-content-tertiary animate-pulse">Loading…</div>
              ) : (() => {
                const current = customers.find((c) => c.id === currentStripeCustomerId);
                if (!current) {
                  return (
                    <div className="text-sm text-content-primary font-mono">
                      {currentStripeCustomerId}
                    </div>
                  );
                }
                const pm = current.paymentMethod;
                return (
                  <div>
                    <div className="text-sm font-medium text-content-primary">
                      {current.name || current.email}
                    </div>
                    <div className="text-xs text-content-tertiary">
                      {current.email}
                      {pm ? ` — ${pm.brand} •••• ${pm.last4}` : ""}
                    </div>
                  </div>
                );
              })()}
            </div>
          )}

          {/* Stripe customer dropdown */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Stripe Customer
            </label>
            {isLoading ? (
              <div className="flex items-center gap-2 py-3 text-sm text-gray-500">
                <div className="w-4 h-4 border-2 border-gray-300 border-t-primary rounded-full animate-spin" />
                Loading Stripe customers...
              </div>
            ) : error ? (
              <div className="text-sm text-red-500 py-2">{error}</div>
            ) : customers.length === 0 ? (
              <div className="text-sm text-gray-500 py-2">
                No Stripe customers found
              </div>
            ) : (
              <div className="relative" ref={dropdownRef}>
                <div
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white focus-within:ring-2 focus-within:ring-primary flex items-center gap-2 cursor-text"
                  onClick={() => {
                    setIsOpen(true);
                    searchInputRef.current?.focus();
                  }}
                >
                  <svg className="w-4 h-4 text-gray-400 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                  <input
                    ref={searchInputRef}
                    type="text"
                    value={isOpen ? searchFilter : (selectedCustomer ? `${formatCustomer(selectedCustomer).name} — ${formatCustomer(selectedCustomer).detail}` : "")}
                    onChange={(e) => {
                      setSearchFilter(e.target.value);
                      setIsOpen(true);
                    }}
                    onFocus={() => {
                      setSearchFilter("");
                      setIsOpen(true);
                    }}
                    placeholder="Search customers..."
                    className="flex-1 bg-transparent outline-none text-sm text-gray-900 placeholder-gray-400"
                  />
                  <svg
                    className={`w-4 h-4 text-gray-400 shrink-0 transition-transform ${isOpen ? "rotate-180" : ""}`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </div>
                {isOpen && (
                  <div className="absolute z-10 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-80 overflow-auto">
                    {filteredCustomers.length === 0 ? (
                      <div className="px-3 py-3 text-sm text-gray-500 text-center">
                        No customers match "{searchFilter}"
                      </div>
                    ) : (
                      filteredCustomers.map((customer) => {
                        const { name, detail } = formatCustomer(customer);
                        const isSelected = customer.id === selectedCustomerId;
                        return (
                          <button
                            key={customer.id}
                            type="button"
                            onClick={() => handleSelect(customer.id)}
                            className={`w-full px-3 py-2.5 text-left text-sm hover:bg-gray-50 border-b border-gray-100 last:border-b-0 ${
                              isSelected ? "bg-primary/5" : ""
                            }`}
                          >
                            <div className="font-medium text-gray-900">
                              {name}
                            </div>
                            <div className="text-xs text-gray-500">{detail}</div>
                          </button>
                        );
                      })
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 flex gap-3 justify-end">
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={
              !selectedCustomerId ||
              isPending ||
              (isRelink && selectedCustomerId === currentStripeCustomerId)
            }
            loading={isPending}
          >
            {isRelink ? "Replace" : "Save"}
          </Button>
        </div>
      </div>
    </div>
  );
}
